import Axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import apiUrls from 'base/api/urls';
import { MediaObject, Mini, Program, Tags } from 'base/api/types';
import { StandardError } from 'base/api/errors';
import { useDetailsQuery, useListQuery, useObjectQuery } from 'base/api/hooks';
import { STATUSES } from 'common/consts';
import { CommonVars, PK } from 'common/types';
import { composeUrl } from 'common/utils';
import { ProgramCategoryMap } from './consts';

export const useProgramsList = () => {
  return useListQuery<Program>(apiUrls.LIFESTYLE_PROGRAMS.LIST, {
    isBusinessSensitive: true,
  });
};

interface ProgramCreateVars {
  title: string;
  description: string;
  short_description: string;
  category: string;
}

export const useProgramCreate = () => {
  const queryClient = useQueryClient();
  return useMutation<Program, StandardError, ProgramCreateVars>(
    (data) => Axios.put(apiUrls.LIFESTYLE_PROGRAMS.LIST, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiUrls.LIFESTYLE_PROGRAMS.LIST);
        queryClient.invalidateQueries(apiUrls.CONTENT.LIST);
      },
    },
  );
};

type ProgramUpdateVars = Partial<ProgramCreateVars & CommonVars> & {
  status?: STATUSES;
};

export const useProgramUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation<Program, StandardError, ProgramUpdateVars>(
    ({ pk, ...data }) =>
      Axios.patch(
        composeUrl(apiUrls.LIFESTYLE_PROGRAMS.DETAILS, { params: { pk } }),
        data,
      ),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(apiUrls.LIFESTYLE_PROGRAMS.LIST);
        queryClient.invalidateQueries(apiUrls.LIFESTYLE_PROGRAMS.TAGS);
        queryClient.setQueryData(
          [apiUrls.LIFESTYLE_PROGRAMS.DETAILS, { pk: data.pk }],
          data,
        );
      },
    },
  );
};

export const useProgramDetails = (pk?: PK) => {
  return useQuery<Program, StandardError>({
    queryKey: [apiUrls.LIFESTYLE_PROGRAMS.DETAILS, { pk }],
    enabled: !!pk,
  });
};

type ProgramDays = {
  days: {
    day: number;
    minis: Mini[];
  }[];
};

export const useProgramDays = (pk?: PK) => {
  return useDetailsQuery<ProgramDays>({
    queryKey: [apiUrls.LIFESTYLE_PROGRAMS.DAYS, { pk }],
    pk,
  });
};

interface ProgramMinisVars extends CommonVars {
  days: {
    minis: PK[];
  }[];
}

export const useProgramAllDaysUpdate = () => {
  return useMutation<unknown, StandardError, ProgramMinisVars>(({ pk, ...data }) =>
    Axios.put(composeUrl(apiUrls.LIFESTYLE_PROGRAMS.DAYS, { params: { pk } }), data),
  );
};

type ProgramMedia = {
  images: MediaObject[];
};

export const useProgramMedia = (pk?: PK) => {
  return useDetailsQuery<ProgramMedia>({
    queryKey: [apiUrls.LIFESTYLE_PROGRAMS.MEDIA, { pk }],
    pk,
  });
};

interface ProgramMediaVars extends CommonVars {
  media_file_pks: PK[];
}

export const useProgramMediaUpdate = () => {
  return useMutation<ProgramMedia, StandardError, ProgramMediaVars>(({ pk, ...data }) =>
    Axios.put(composeUrl(apiUrls.LIFESTYLE_PROGRAMS.MEDIA, { params: { pk } }), data),
  );
};

export const useProgramTags = (pk?: PK) => {
  return useDetailsQuery<Tags>({
    pk,
    queryKey: [apiUrls.LIFESTYLE_PROGRAMS.TAGS, { pk }],
  });
};

interface ProgramTagsUpdateVars extends CommonVars {
  payload: Tags;
}

export const useProgramTagsUpdate = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, StandardError, ProgramTagsUpdateVars>(
    ({ pk, ...payload }) =>
      Axios.put(composeUrl(apiUrls.LIFESTYLE_PROGRAMS.TAGS, { params: { pk } }), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiUrls.LIFESTYLE_PROGRAMS.TAGS);
      },
    },
  );
};

export const useProgramCategoryList = () => {
  return useObjectQuery<ProgramCategoryMap>(apiUrls.LIFESTYLE_PROGRAMS.CATEGORIES, {
    staleTime: Infinity,
  });
};
